/* eslint-disable react/sort-comp */
// @flow
import React, { PureComponent } from 'react';
import fetchJsonp from 'fetch-jsonp';
import idx from 'idx';
import RexButton from 'rex-button';

import ButtonSupplimentaryText, {
  ButtonSupplimentaryTextNotice,
  ButtonSupplimentaryTextLink,
} from './ButtonSupplimentaryText/ButtonSupplimentaryText';
import Above from './Above/Above';
import styles from './App.scss';
import Below from './Below/Below';
import InformationText from './InformationText/InformationText';
import ValidationText from './ValidationText';
import {
  theme,
  validationTextMap,
  apiCheckURL,
  apiApplyURL,
  makeLoginRedirectURL,
  buttonStateMap,
  buttonStateText,
  REDIRECTION_DELAY_AFTER_SUCCESS,
  REDIRECTION_DELAY_AFTER_IN_PROCESS,
} from '../constant';
import type { buttonPropsType, buttonPropsRedirectionType, responseType } from './types';
import {
  isValidState,
  sendRAT,
  isDisabled,
  buttonStateFactory,
  validationTextFactory,
  isCheckOK,
  redirectToUrl,
  sendInitialRAT,
  getIsLoggedIn,
  informationTextFactory,
} from '../util';

declare var __DEV__: boolean;

export default class RexCampaignButton extends PureComponent<buttonPropsType, void> {
  isCheckboxMandatory: boolean;
  isRedirectionEnabled: boolean;
  theme: string;
  below: ?Object;
  redirection: ?buttonPropsRedirectionType;

  constructor(props: buttonPropsType) {
    super(props);

    this.theme = idx(props, _ => _.rexButton.theme) || theme.RED;
    this.below = idx(props, _ => _.rexButton.below);
    this.redirection = idx(props, _ => _.rexButton.redirection);

    this.isCheckboxMandatory = !!this.below && this.below.isCheckboxMandatory;
    this.isRedirectionEnabled = !!this.redirection && this.redirection.enabled === true;
  }

  componentDidMount() {
    if (this.props.shouldCheck) {
      // Send RAT
      window.addEventListener('load', () => {
        const ratForm: HTMLInputElement = (document.getElementById('ratAccountId'): any);
        if (ratForm) {
          const { cId, id: mountId } = this.props.rexButton;
          const ratAccountIdForCampaign = ratForm.value;
          sendInitialRAT(cId, mountId, ratAccountIdForCampaign);
        }
      });

      // Call check API
      this.checkApplyStatus();
    }
  }

  componentDidUpdate() {
    const { buttonState: { buttonState } } = this.props;

    if (buttonState === buttonStateMap.IN_PROCESS && this.isRedirectionEnabled && this.redirection) {
      // Redirect to URL
      const { url } = this.redirection;
      redirectToUrl(url, REDIRECTION_DELAY_AFTER_IN_PROCESS).then(() => {
        if (this.props.shouldCheck) {
          // Check apply status to avoid flashing, and in case of anchor redirection
          this.checkApplyStatus();
        }
      });
    }
  }

  onCheckChange = (e: SyntheticEvent<HTMLInputElement>) => {
    const { rexButton: { cId }, updateButtonState } = this.props;
    const newState = {}; // this is made empty intentionally, because flowtype would seal an non-empty object

    if (this.below && this.isCheckboxMandatory) {
      newState.buttonState = e.currentTarget.checked ? buttonStateMap.ENABLED : buttonStateMap.TOC_NOT_CHECKED;
    }

    newState.isChecked = e.currentTarget.checked;

    updateButtonState(newState, cId);
  };

  checkApplyStatus = () => {
    const { rexButton: { cId }, updateButtonState } = this.props;

    // Cached state
    if (window.localStorage && isValidState(localStorage.getItem(`rex-campaign-${cId}`))) {
      const cachedState = localStorage.getItem(`rex-campaign-${cId}`);

      updateButtonState(
        {
          buttonState: cachedState,
        },
        cId
      );

      return;
    }

    // if there's no cached state, start the apply status check
    updateButtonState({ buttonState: buttonStateMap.LOADING }, cId);

    fetchJsonp(`${apiCheckURL}${cId}`)
      .then(res => res.json())
      .then(res => {
        const { buttonState: { isChecked } } = this.props;

        // code is OK and not yet applied
        if (isCheckOK(res)) {
          // logged-in status, should be true
          const isLoggedIn = getIsLoggedIn(res);

          // we don't need validation/information text for totally successful case
          // just the buttonState would suffice
          if (this.below && this.isCheckboxMandatory && !isChecked) {
            updateButtonState(
              {
                buttonState: buttonStateMap.TOC_NOT_CHECKED,
                isLoggedIn,
              },
              cId
            );
          } else {
            updateButtonState(
              {
                buttonState: buttonStateMap.ENABLED,
                isLoggedIn,
              },
              cId
            );
          }
        } else {
          this.resActionFactory(res);
        }
      })
      .catch(err => {
        if (__DEV__) {
          console.error(err);
        }

        if (this.below && this.isCheckboxMandatory && !this.props.isChecked) {
          updateButtonState(
            {
              buttonState: buttonStateMap.TOC_NOT_CHECKED,
              validationText: validationTextMap.FALLBACK,
            },
            cId
          );
        } else {
          updateButtonState(
            {
              buttonState: buttonStateMap.ENABLED,
              validationText: validationTextMap.FALLBACK,
            },
            cId
          );
        }
      });
  };

  /**
   * resActionFactory
   * will be called after calling Check API and Entry API both
   */
  resActionFactory = (res: responseType) => {
    const { rexButton: { id, cId, onEntrySuccess }, updateButtonState, buttonState: { isChecked } } = this.props;
    const isLoggedIn = getIsLoggedIn(res);
    let newButtonState = buttonStateFactory(res);

    // TODO: it's better to do refactoring here
    if (newButtonState === buttonStateMap.ENABLED && !isLoggedIn && this.isCheckboxMandatory && !isChecked) {
      newButtonState = buttonStateMap.TOC_NOT_CHECKED;
    }

    // Cache state in localStorage
    if (window.localStorage) {
      switch (newButtonState) {
        case buttonStateMap.SUCCESS:
        case buttonStateMap.ALREADY_APPLIED: {
          localStorage.setItem(`rex-campaign-${cId}`, 'ALREADY_APPLIED');
          break;
        }
        // MEMO: UXDCOECEB-23 stopped caching 'EXPIRE' state.
        // To resume this, need to tweak `buttonStateFactory`
        // case buttonStateMap.EXPIRED: {
        //   localStorage.setItem(`rex-campaign-${cId}`, 'EXPIRED');
        //   break;
        // }
        default:
          break;
      }
    }

    if (newButtonState === buttonStateMap.SUCCESS) {
      // Fire a callback for success
      if (typeof onEntrySuccess === 'function') {
        onEntrySuccess();
      }

      // sendRAT
      sendRAT(cId, true, id);

      // In case of redirection is enabled, reserve the next actions
      if (this.isRedirectionEnabled && this.redirection) {
        // Set buttonState to IN_PROCESS state
        setTimeout(() => {
          updateButtonState({ buttonState: buttonStateMap.IN_PROCESS }, cId);
        }, REDIRECTION_DELAY_AFTER_SUCCESS);
      }
    }

    updateButtonState(
      {
        buttonState: newButtonState,
        validationText: validationTextFactory(res),
        informationText: informationTextFactory(res),
        isLoggedIn,
      },
      cId
    );
  };

  applyForCampaign = () => {
    const {
      updateButtonState,
      buttonState: { isChecked, isLoggedIn },
      rexButton: { id, cId, cCode, onEntry, onEntryFail },
    } = this.props;
    /* eslint-disable */
    /*
    {
      code: "200",
      message: "OK",
      result: "2"
    }

    [message]

      OK: Normal
      Server Error: In Trouble
      Invalid Parameter: something wrong with parameter

    [campaign entry's result]

      0: complete entry
      1: already entered
      2: out side of entry period
      3: can not enter due to not allowed device

      when not found campaign, "not found campaign" is on it.
    */
    /* eslint-enable */
    if (!isLoggedIn) {
      // TODO: If it's required, need to add sendRAT as success
      // TODO: it might be better to add this after calling Entry API too.
      window.location.href = `${makeLoginRedirectURL(cCode)}?${isChecked ? 'ekey=checked' : 'ekey=unchecked'}`;

      return;
    }

    updateButtonState({ buttonState: buttonStateMap.LOADING }, cId);

    if (typeof onEntry === 'function') {
      onEntry();
    }

    // apply API
    fetchJsonp(`${apiApplyURL}${cCode}${isChecked ? '&ekey=checked' : '&ekey=unchecked'}`)
      .then(res => res.json())
      .then(this.resActionFactory, () => {
        if (typeof onEntryFail === 'function') {
          onEntryFail();
        }

        sendRAT(cId, false, id);
        updateButtonState({ buttonState: buttonStateMap.RETRY }, cId);
      });
  };

  ifShowStatements(): boolean {
    const { buttonState } = this.props;

    if (
      buttonState.buttonState === buttonStateMap.LOADING ||
      buttonState.buttonState === buttonStateMap.ALREADY_APPLIED ||
      buttonState.buttonState === buttonStateMap.EXPIRED ||
      buttonState.buttonState === buttonStateMap.IN_PROCESS ||
      buttonState.buttonState === buttonStateMap.SUCCESS
    ) {
      return false;
    }

    return true;
  }

  renderAbove = () => {
    const { rexButton: { above } } = this.props;

    if (this.ifShowStatements() && above) {
      return <Above {...above} theme={this.theme} />;
    }

    return null;
  };

  renderBelow = () => {
    const { buttonState: { isChecked }, rexButton: { id, below } } = this.props;

    if (this.ifShowStatements() && below) {
      return (
        <Below
          {...below}
          id={id}
          onCheckChange={this.onCheckChange}
          cId={this.props.rexButton.cId}
          isChecked={isChecked}
          theme={this.theme}
        />
      );
    }

    return null;
  };

  ifShowButtonSupplimentaryText(): boolean {
    const { buttonState: { buttonState, isLoggedIn } } = this.props;

    if (buttonState !== buttonStateMap.ALREADY_APPLIED && (!isLoggedIn || buttonState === buttonStateMap.EXPIRED)) {
      return false;
    }

    return true;
  }

  renderButtonSupplimentaryText = () => {
    const { buttonState: { buttonState } } = this.props;

    if (this.ifShowButtonSupplimentaryText() && this.isRedirectionEnabled && this.redirection) {
      const { url, pageTitle } = this.redirection;
      const { linkText = `${pageTitle}${buttonStateText.IN_PROCESS}` } = this.redirection;
      const type =
        buttonState === buttonStateMap.ALREADY_APPLIED ? ButtonSupplimentaryTextLink : ButtonSupplimentaryTextNotice;
      const noticeText = `${pageTitle}${buttonStateText.IN_PROCESS}`;

      return (
        <ButtonSupplimentaryText
          type={type}
          noticeText={noticeText}
          linkText={linkText}
          linkUrl={url}
          theme={this.theme}
        />
      );
    }

    return null;
  };

  render() {
    const { buttonState } = this.props;

    if (!buttonState || !buttonState.buttonState) {
      return null;
    }

    const buttonText =
      // ALREADY_APPLIED
      (buttonState.buttonState === buttonStateMap.ALREADY_APPLIED &&
        idx(this.props, _ => _.rexButton.alreadyAppliedText)) ||
      // IN_PROCESS
      (buttonState.buttonState === buttonStateMap.IN_PROCESS &&
        this.isRedirectionEnabled &&
        this.redirection &&
        `${this.redirection.pageTitle}${buttonStateText.IN_PROCESS}...`) ||
      // Default texts
      buttonStateText[buttonState.buttonState];

    return (
      <div id={styles.rex_campaign_btn}>
        {this.renderAbove()}
        <ValidationText buttonState={buttonState.buttonState} validationText={buttonState.validationText} />
        <RexButton
          theme={this.theme}
          buttonState={buttonState.buttonState}
          onClick={this.applyForCampaign}
          text={buttonText}
          hasArrow={this.isRedirectionEnabled}
          isDisabled={isDisabled(buttonState.buttonState)}
        />
        {this.renderButtonSupplimentaryText()}
        <InformationText buttonState={buttonState.buttonState} informationText={buttonState.informationText} />
        {this.renderBelow()}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/App.js